import type {
  CoaContractId,
  OrderId,
  OrganizationId,
  ScenarioTimeline_CoaFragment,
  ScenarioTimeline_OrganizationFragment,
  ScenarioTimeline_PortCallFragment,
  ShipId,
} from '../../graphql';
import type { Range } from '../../lib/util';
import {
  assertDefined,
  filteredMap,
  get,
  isDefined,
  isInRange,
  mapKeyBy,
  NONE,
  sortBy,
} from '../../lib/util';
import type { LocationId, PortCallData } from './Core';
import {
  consecutiveCoaVoyagesEnabled,
  EventCategory,
  getEventLocation,
} from './Core';
import {
  excludeDryDockPeriods,
  getEventLocationId,
  isDryDockEventTimeData,
  isOffHireEventTimeData,
  isStaticEvent,
} from './TimeData';
import type { Vessel } from './Vessel';
import { isEligibleForAutomaticPlanning } from './Vessel';
import type {
  CargoEvent,
  CoaEvent,
  Event,
  Leg,
  SpotEvent,
  Events,
  EventsAndLegs,
  WaitEvent,
  CoaTimeDataGroup,
  SpotTimeDataGroup,
  TcTimeDataGroup,
  TimeDataInputs,
  TimeDataTotals,
  TimeData,
} from './TimeData';
import {
  PortCallEventType,
  getBoundaryPairPreviousPortCallId,
  isOffHireEvent,
  hasOrder,
  getEventEndLocationId,
  getEventStartLocationId,
  isLeg,
  isNotTimeData,
  isPortCallArrivalEventTimeData,
  isTimeData,
  ImplicitEventType,
  isCargoEventTimeData,
  isPortCallEvent,
  isPortCallEventTimeData,
} from './TimeData';
import type { ScheduleDataContext } from './ScheduleDataContext';
import { hToMs } from '../../lib/time';
import { getOverlappingEvents, getPreferredCapacityQuantity } from './util';
import uniqBy from 'lodash/uniqBy';
import findLastIndex from 'lodash/findLastIndex';
import groupBy from 'lodash/groupBy';
import { getEventShipmentId } from '../shipment/shipment';
import type { ShipmentModalNavTarget } from '../shipment/shipment';
import type { ClientId } from './ClientId';
import { generateClientId } from './ClientId';
import type { ScenarioOrder, ScenarioShipment } from '../../lib/planningData';
import type { DefaultMap } from '@seaber/common/lib/collections';
import { mapGroupBy, mapGroupBy2 } from '@seaber/common/lib/collections';
import { assignNonOverlappingEventsToNextShipment } from '../timeline/utils/utils';
import { freeEventFilter, isLegOrWait } from '../timeline/utils/utils';

let warnedConsecutiveCoaMultipleGroups = false;

/**
 * Defines how shipments should behave in relation to others on the same timeline.
 * When true, shipments don't affect each other in any way, they are treated as isolated and individual timelines themselves.
 * When false, a shipment will have to for example wait for previous shipment to complete and there will be ballast in between.
 * Note: TC is not wired to use this setting currently. The TC setting is here more for documentation.
 */
export const shipmentIsolationSetting: {
  [T in EventTimeline['type']]: { withVessel: boolean; withoutVessel: boolean };
} = {
  coa: { withVessel: true, withoutVessel: true },
  spot: { withVessel: true, withoutVessel: true },
  tc: { withVessel: false, withoutVessel: true },
};

export const getSubTimeline = <T extends Events>(
  timeline: EventTimeline<T>,
  focus?: TimelineFocus, // OR filter, only one of the focus params needs to match for an event to be included in the resulting timeline
): EventTimeline<T> => {
  if (!focus) {
    return timeline;
  }
  const { shipmentIds, orderIds, time } = focus;
  // On contracts without vessel assignment we assume that each shipment is carried on a vessel of their own, with no interaction with other orders.
  // For COA vessel, isolation is decided at runtime per vessel. When consecutive voyages are enabled, shipments are not isolated.
  const withVesselIsolated =
    timeline.type === 'coa' && timeline.vessel !== null
      ? !consecutiveCoaVoyagesEnabled(timeline.vessel)
      : shipmentIsolationSetting[timeline.type].withVessel;
  const isolatedShipments =
    (timeline.vesselId === null
      ? shipmentIsolationSetting[timeline.type].withoutVessel
      : withVesselIsolated) && (focus.orderIds ?? []).length == 0;

  if (
    orderIds === undefined &&
    shipmentIds === undefined &&
    time === undefined
  ) {
    console.warn(
      `Requested sub timeline with focus but focus has no selective properties present`,
    );
    return timeline;
  }

  const newVoyageGroups = timeline.voyageGroups
    .filter(
      (voyageGroup) =>
        (!orderIds && !shipmentIds) ||
        voyageGroup.orders.some(
          (o) =>
            orderIds?.includes(o.id) ||
            (o.shipment?.clientId &&
              shipmentIds?.includes(o.shipment.clientId)),
        ),
    )
    .map((voyageGroup) => {
      const { eventsAndLegs } = voyageGroup;

      const shipmentsStartingOutsideRange = new Set<ClientId>();
      const portCallsStartingWithinTimeRange = new Set<ClientId>();
      const offHiresStartingWithTimeRange = new Set<ClientId>();

      const focusedOrderTds: TimeData<CargoEvent>[] = eventsAndLegs.flatMap(
        (td, i) => {
          if (!isTimeData(td)) return [];

          const prev = eventsAndLegs[i - 1];
          const prevBallastLegStart =
            prev && isLeg(prev) && !prev.laden ? prev.start : null;

          if (
            time?.startingWithinRangeOnly &&
            isInRange(prevBallastLegStart || td.start, time.range)
          ) {
            if (
              isPortCallEventTimeData(td) &&
              td.event.portCallId !== null &&
              td.event.event === PortCallEventType.Arrival
            ) {
              // If startingWithinRangeOnly is true, we don't want partial shipments to be
              // included in the resulting sub timeline.
              // We include a complete shipment in the result if its' initial ballast
              // leg (or first arrival if no ballast leg) starts within the range
              // thus we need to separately track which port calls start within the range.
              portCallsStartingWithinTimeRange.add(td.event.portCallId);
            } else if (time.includeOffHires && isOffHireEventTimeData(td)) {
              offHiresStartingWithTimeRange.add(td.event.entry.clientId);
            }
          }

          if (!isCargoEventTimeData(td)) return [];

          const { shipmentId } = td.event.order;

          if (time) {
            if (
              time.startingWithinRangeOnly &&
              shipmentId &&
              td.event.portCallId &&
              !shipmentsStartingOutsideRange.has(shipmentId) &&
              portCallsStartingWithinTimeRange.has(td.event.portCallId)
            ) {
              return [td];
            } else if (
              !time.startingWithinRangeOnly &&
              (isInRange(td.start, time.range) || isInRange(td.end, time.range))
            ) {
              return [td];
            }

            if (time.startingWithinRangeOnly && shipmentId != null) {
              shipmentsStartingOutsideRange.add(shipmentId);
            }
          }

          if (orderIds?.includes(td.event.order.id)) {
            return [td];
          }

          if (
            td.event.order.shipment?.clientId &&
            shipmentIds?.includes(td.event.order.shipment.clientId)
          ) {
            return [td];
          }

          return [];
        },
      );

      const focusedPortCallIds = new Set(
        focusedOrderTds.map((td) => td.event.portCallId),
      );
      const focusedOrderIds = new Set(
        focusedOrderTds.map((td) => td.event.order.id),
      );
      focusedPortCallIds.delete(null);

      const isFocusedEvent = (td: TimeData) =>
        isTimeData(td) &&
        (focusedPortCallIds.has(td.event.portCallId) ||
          (isCargoEventTimeData(td) &&
            focusedOrderIds.has(td.event.order.id)) ||
          (isOffHireEventTimeData(td) &&
            offHiresStartingWithTimeRange.has(td.event.entry.clientId)));

      let currentStartIdx = eventsAndLegs.findIndex(isFocusedEvent);
      let currentEndIdx = findLastIndex(eventsAndLegs, isFocusedEvent);

      if (currentStartIdx === -1 || currentEndIdx === -1) {
        // This can happen if focusing orders which are not (anymore?) on the timeline. Especially when viewing a shipment modal and unassigning.
        // Make sure both ends are at an index where following steps will find nothing to include in the set
        currentStartIdx = eventsAndLegs.length;
        currentEndIdx = -1;
      }

      const includeBorderLegs = timeline.vesselId !== null;

      let legOrOtherOrderFound = false;
      // Extend to the past from the first order in shipment to include any preceding single events and legs but no more - and exclude even legs if they're not on the same vessel
      // Updated rules: also stop at arrival to port to fix including unrelated events happening during the same port call
      for (let i = currentStartIdx - 1; i >= 0; i--) {
        const td = eventsAndLegs[i];
        if (isLeg(td)) {
          legOrOtherOrderFound = true;
          if (!includeBorderLegs) {
            break;
          }
        } else if (isTimeData(td) && isCargoEventTimeData(td)) {
          // Other order should never be included
          legOrOtherOrderFound = true;
          break;
        } else if (
          isTimeData(td) &&
          isPortCallEventTimeData(td) &&
          td.event.event === PortCallEventType.Arrival
        ) {
          // Arrival found, include this one and stop looking
          currentStartIdx = i;
          break;
        } else if (legOrOtherOrderFound) {
          break;
        }
        currentStartIdx = i;
      }

      // Extend to the future from the last order in shipment until the next leg (or unrelated cargo event) which is not relevant to this shipment anymore
      // Updated rules: also stop at departure from port to fix including unrelated events happening during the same port call
      for (let i = currentEndIdx + 1; i < eventsAndLegs.length; i++) {
        const td = eventsAndLegs[i];
        if (isLeg(td) || (isTimeData(td) && isCargoEventTimeData(td))) {
          // Leg or other order found, not to be included
          break;
        } else if (
          isTimeData(td) &&
          isPortCallEventTimeData(td) &&
          td.event.event === PortCallEventType.Departure
        ) {
          // Departure found, include and stop looking
          currentEndIdx = i;
          break;
        }
        currentEndIdx = i;
      }

      // Collect all events and legs in the range except those that belong to orders of other shipments
      const selectedEventsAndLegs: EventsAndLegs = [];

      for (let i = currentStartIdx; i <= currentEndIdx; ++i) {
        const td = eventsAndLegs[i];
        if (!td) {
          continue;
        }

        const matchesFocus =
          (shipmentIds === undefined && orderIds === undefined) ||
          !isTimeData(td) ||
          (!isCargoEventTimeData(td) &&
            focusedPortCallIds.has(td.event.portCallId)) ||
          (isCargoEventTimeData(td) &&
            (orderIds?.includes(td.event.order.id) ||
              (td.event.order.shipment?.clientId &&
                shipmentIds?.includes(td.event.order.shipment.clientId))));

        // Filter out all cargo events from other shipments if startingWithinRangeOnly is true
        const shipmentInTimeScope =
          !time?.startingWithinRangeOnly ||
          !isTimeData(td) ||
          !isCargoEventTimeData(td) ||
          focusedOrderIds.has(td.event.order.id);

        if ((matchesFocus || !isolatedShipments) && shipmentInTimeScope) {
          selectedEventsAndLegs.push(td);
        }
      }
      const selectedTds = selectedEventsAndLegs.filter(isTimeData);
      const orders = uniqBy(
        selectedTds.flatMap((td) =>
          hasOrder(td.event) ? [td.event.order] : [],
        ),
        'id',
      );

      const portCallsByID = mapKeyBy(voyageGroup.portCalls, (p) => p.id);
      const portCalls = uniqBy(
        selectedTds
          .map((td) => {
            const pcId = td.event.portCallId;
            return pcId && portCallsByID.get(pcId);
          })
          .filter(isDefined),
        (p) => p.id,
      );

      const res: VoyageGroup = {
        eventsAndLegs: selectedEventsAndLegs,
        events: selectedTds,
        orders,
        portCalls,
        legs: selectedEventsAndLegs.filter(isLeg),
        totals: voyageGroup.totals, // TODO: maybe need to recalculate for this sub selection
        inputs: voyageGroup.inputs,
        vessel: voyageGroup.vessel,
      };

      return res;
    });

  return {
    ...timeline,
    isPartial: true,
    isCollapsed: false,
    canCollapse: false,
    parent: timeline,
    voyageGroups: newVoyageGroups,
  };
};

/**
 * Enhances voyage groups by including non-overlapping preceding free events
 * that should be part of the next shipment based on their temporal relationship.
 *
 * This function:
 * 1. Identifies all shipments in the timeline
 * 2. Finds free events (single events not tied to orders) and legs/wait events
 * 3. Assigns non-overlapping free events to the next shipment they precede
 * 4. Merges these events into the appropriate voyage groups
 *
 * @param focusedVoyageGroups - The voyage groups to enhance
 * @param allVoyageGroups - All voyage groups from the full timeline (for context)
 * @returns Enhanced voyage groups with free events included
 */
export function enhanceVoyageGroupsWithFreeEvents(
  focusedVoyageGroups: VoyageGroup[],
  allVoyageGroups: VoyageGroup[],
  focusedShipmentClientId?: ClientId | null,
): VoyageGroup[] {
  const allCargoEvents: TimeData<CargoEvent>[] = [];
  const allFreeEvents: EventsAndLegs[number][] = [];
  const allPortCallsById = new Map<ClientId, PortCallData>();

  for (const vg of allVoyageGroups) {
    for (const eal of vg.eventsAndLegs) {
      if (isTimeData(eal) && isCargoEventTimeData(eal))
        allCargoEvents.push(eal);
      if (isLegOrWait(eal) || freeEventFilter(eal)) allFreeEvents.push(eal);
    }

    for (const pc of vg.portCalls) {
      if (pc.id) allPortCallsById.set(pc.id, pc);
    }
  }

  const eventsByShipment = groupBy(
    allCargoEvents,
    (e) => getEventShipmentId(e.event) ?? '',
  );

  const sortedShipments = sortBy(
    Object.values(eventsByShipment),
    (x) => x[0].start,
  );

  const orderIdToShipmentIndex = new Map<number, number>();
  // Keyed by both the entry-override and the persisted order.shipmentId so callers can pass either.
  const shipmentIdToIndex = new Map<ClientId, number>();
  sortedShipments.forEach((shipment, index) => {
    shipment.forEach((event) => {
      orderIdToShipmentIndex.set(event.event.order.id, index);
      const entryShipmentId = getEventShipmentId(event.event);
      if (entryShipmentId) shipmentIdToIndex.set(entryShipmentId, index);
      const orderShipmentId = event.event.order.shipmentId;
      if (orderShipmentId) shipmentIdToIndex.set(orderShipmentId, index);
    });
  });

  // Assign non-overlapping events to shipments
  const nonOverlappingEventsByShipment =
    assignNonOverlappingEventsToNextShipment(
      sortedShipments.map((shipment) => {
        const portCallIds: Set<ClientId> = new Set();
        shipment.forEach(({ event }) => {
          if (event.portCallId) {
            portCallIds.add(event.portCallId);
          }
        });
        return {
          start: shipment[0].start,
          end: shipment[shipment.length - 1].end,
          portCallIds,
        };
      }),
      allFreeEvents,
    );

  // Merge additional events with focused voyage groups
  const enhancedVoyageGroups = focusedVoyageGroups.map((vg) => {
    let shipmentIndex: number | undefined;
    if (focusedShipmentClientId != null) {
      // Caller-supplied shipment is authoritative; do not fall back on miss.
      shipmentIndex = shipmentIdToIndex.get(focusedShipmentClientId);
    } else {
      // Pre-existing fallback used by ScenarioComparison (time-only focus).
      const fallback = vg.orders.find((o) => orderIdToShipmentIndex.has(o.id));
      shipmentIndex = fallback
        ? orderIdToShipmentIndex.get(fallback.id)
        : undefined;
    }

    const additionalEventsForThisVG =
      shipmentIndex !== undefined
        ? nonOverlappingEventsByShipment.get(shipmentIndex) || []
        : [];

    const enhancedEventsAndLegs = uniqBy(
      sortBy(
        [...vg.eventsAndLegs, ...additionalEventsForThisVG],
        (e) => e.start,
      ),
      (e) => e.key,
    );
    // Extract TimeData events from the combined events and legs
    const enhancedEvents = enhancedEventsAndLegs.filter(isTimeData);

    // Extract unique orders from events that have orders
    const visitedOrderIds = new Set<number>();
    const enhancedOrders = filteredMap(enhancedEvents, (td) => {
      if (!hasOrder(td.event)) return NONE;
      const order = td.event.order;
      if (visitedOrderIds.has(order.id)) return NONE;
      visitedOrderIds.add(order.id);
      return order;
    });

    // Extract unique port calls referenced by the enhanced events
    const enhancedPortCalls = uniqBy(
      enhancedEvents
        .map((td) => {
          const pcId = td.event.portCallId;
          return pcId && allPortCallsById.get(pcId);
        })
        .filter(isDefined),
      (p) => p.id,
    );

    const result: VoyageGroup = {
      eventsAndLegs: enhancedEventsAndLegs,
      events: enhancedEvents,
      legs: enhancedEventsAndLegs.filter(isLeg),
      orders: enhancedOrders,
      portCalls: enhancedPortCalls,
      totals: vg.totals,
      inputs: vg.inputs,
      vessel: vg.vessel,
    };

    return result;
  });

  return enhancedVoyageGroups;
}

/**
 * Gets a focused sub-timeline with enhanced voyage groups that include
 * non-overlapping free events assigned to their next shipment.
 *
 * This combines getSubTimeline filtering with free event enhancement
 * to ensure all components receive consistent voyage group data.
 *
 * @param timeline - The full event timeline
 * @param focus - Optional focus filter for orders/shipments/time
 * @returns Enhanced focused sub-timeline with free events included
 */
export const getEnhancedSubTimeline = <T extends Events>(
  timeline: EventTimeline<T>,
  focus?: TimelineFocus,
  focusedShipmentClientId?: ClientId | null,
): EventTimeline<T> => {
  const subTimeline = getSubTimeline(timeline, focus);
  const allVoyageGroups =
    timeline.parent?.voyageGroups || timeline.voyageGroups;
  const enhancedVoyageGroups = enhanceVoyageGroupsWithFreeEvents(
    subTimeline.voyageGroups,
    allVoyageGroups,
    focusedShipmentClientId,
  );
  return {
    ...subTimeline,
    voyageGroups: enhancedVoyageGroups,
  };
};

// Shipments within a shipment group can affect each others timings - different shipment groups cannot affect each other
export type VoyageGroup<EventType extends Events = Events> = {
  events: TimeData<EventType>[];
  legs: Array<Leg>;
  portCalls: PortCallData[];
  orders: ScenarioOrder[];
  eventsAndLegs: Array<EventsAndLegs<EventType>[number]>;
  totals: TimeDataTotals | null;
  inputs: TimeDataInputs | null;
  vessel: Vessel | null; // Redundant with EventTimeline but lets us not pass around the timeline object when we have only the VG
};

/** Remove dry dock periods from a VoyageGroup's events, eventsAndLegs, legs, and portCalls. */
export function excludeDryDockPeriodsFromVoyageGroup(
  vg: VoyageGroup,
): VoyageGroup {
  const filteredEvents = excludeDryDockPeriods(vg.events);
  if (filteredEvents.length === vg.events.length) return vg; // nothing removed

  const ealTimeData = vg.eventsAndLegs.filter(isTimeData);
  const filteredEalTimeData = excludeDryDockPeriods(ealTimeData);
  const removed = new Set(
    ealTimeData.filter((e) => !filteredEalTimeData.includes(e)),
  );
  const filteredEventsAndLegs = vg.eventsAndLegs.filter((e, i) => {
    if (isTimeData(e)) return !removed.has(e);
    // Remove legs leading TO the dry dock (next event was removed)
    const next = vg.eventsAndLegs[i + 1];
    return !(next && isTimeData(next) && removed.has(next));
  });
  const keptPortCallIds = new Set(
    filteredEvents.map((e) => e.event.portCallId).filter(Boolean),
  );
  return {
    ...vg,
    events: filteredEvents,
    eventsAndLegs: filteredEventsAndLegs,
    legs: filteredEventsAndLegs.filter(isLeg),
    portCalls: vg.portCalls
      .filter((pc) => pc.id !== null && keptPortCallIds.has(pc.id))
      .map((pc) => {
        // A shared port call keeps its id but must shed the dry dock td so
        // downstream cost sums (e.g. port bunker duration) ignore it.
        const filteredTds = pc.tds.filter((td) => !isDryDockEventTimeData(td));
        return filteredTds.length === pc.tds.length
          ? pc
          : { ...pc, tds: filteredTds };
      }),
  };
}

export type EventTimeline<EventType extends Events = Events> = {
  id: string;
  title: string;
  subtitle: string | null;
  /** When we have focused a subsection of a timeline. Not used currently but seems like a good idea to mark it somehow. */
  isPartial: boolean;
  canCollapse?: boolean;
  isCollapsed?: boolean;
  voyageGroups: VoyageGroup[];
  parent?: EventTimeline<EventType>;
  estimateIdOrScenario: ClientId | 'scenario';
} & (
  | {
      type: 'tc';
      vesselId: ShipId;
      vessel: Vessel;
    }
  | ({
      type: 'coa';
      coa: ScenarioTimeline_CoaFragment;
      coaId: CoaContractId;
      vessel: Vessel | null;
      vesselId: ShipId | null;
      ownerOrganizationId: OrganizationId | null;
      canCollapse: boolean;
      isCollapsed: boolean;
    } & (
      | {
          vesselTimelineIds: string[]; // If parent, has these children contract lines
        }
      | {
          contractTimelineId: string; // If child, has this parent contract line
        }
    ))
  | ({
      type: 'spot';
      ownerOrganizationId: OrganizationId | null;
      vessel: Vessel | null;
      vesselId: ShipId | null;
      canCollapse: boolean;
      isCollapsed: boolean;
    } & (
      | {
          vesselTimelineIds: string[]; // If parent has these children contract lines
        }
      | {
          contractTimelineId: string; // If child has this parent contract line
        }
    ))
);

export type TCEventTimeline = EventTimeline & { type: 'tc' };

export const isTCTimeline = (
  timeline: EventTimeline,
): timeline is TCEventTimeline => timeline.type === 'tc';

export type EventTimelineTimeDataOnly = Pick<
  EventTimeline,
  'type' | 'vessel' | 'estimateIdOrScenario'
> & {
  voyageGroups: {
    events: TimeData[];
  }[];
};

export type TimelineFocus = {
  timelineId: EventTimeline['id'];
  orderIds?: OrderId[];
  shipmentIds?: Array<ClientId | null>;
  time?: {
    /** Include off-hire events starting before first shipment or after last shipment when using startingWithinRangeOnly */
    includeOffHires?: boolean;
    range: Range;
    /**  Only include shipments that start within the timerange */
    startingWithinRangeOnly?: boolean;
  };
  /**  Used for default tab when opening modal */
  navTarget?: ShipmentModalNavTarget;
};

export const getOrdersFromTimeDatas = (tds: TimeData[]): ScenarioOrder[] =>
  uniqBy(
    tds.filter(isCargoEventTimeData).map((td) => td.event.order),
    (o) => o.id,
  );

const mergeTimelinesTo = <T extends Events>(
  target: EventTimeline<T>,
  sources: EventTimeline<T>[],
): EventTimeline<T> => {
  target.voyageGroups.push(...sources.flatMap((s) => s.voyageGroups));
  return target;
};

export const generatePortCallsLegsAndWaits = (
  tds: TimeData[],
  locations: ScheduleDataContext['locations'],
): {
  portCalls: PortCallData[];
  legs: Array<Leg>;
  eventsAndLegs: Array<EventsAndLegs[number]>;
} => {
  /** About port calls: currently by the time this method is called, port call id's are already assigned by
   * the assignPortCallIdsToEvents method and the same operation here is unnecessary. This method only needs to generate
   * those PortCallData objects. However it seems better to ensure the id's are valid here anyway. Also this way any
   * events generated after id's were assigned onto persistable events get proper port call id's as well which may be useful.
   * */
  const ADD_IMPLICIT_WAIT_THRESHOLD = 10;

  let time = 0;
  const legs: Leg[] = [];
  const portCalls: PortCallData[] = [];
  let portCall: PortCallData | null = null;

  const eventsAndLegs: (Leg | TimeData)[] = [];

  // When events are separated from a single port call we need to make sure that both events can't still refer to the same port call
  const usedPortCallIds = new Set<ClientId>();
  const persistedPortCallIds = new Set<ClientId>();
  const l = tds.length;
  for (let i = 0; i < l; ++i) {
    const id = tds[i].event.portCallId;
    if (id) persistedPortCallIds.add(id);
  }

  for (let i = 0; i < l; ++i) {
    const td = tds[i];
    const prevTd = get(tds, i - 1) ?? null;
    const {
      start,
      end,
      event,
      index,
      distance,
      hoursAtSea,
      calculatedSeaDuration,
      laden,
      key,
    } = td;
    const locationId = getEventStartLocationId(event);
    const previousLocationId: LocationId | null =
      prevTd && getEventEndLocationId(prevTd.event);

    if (portCall === null) {
      // Only the very first row ends up here
      portCall = {
        id: event.portCallId ?? null,
        locationId,
        tds: [td],
      };
      portCalls.push(portCall);
    }

    if (previousLocationId === null) {
      eventsAndLegs.push(td);
      time = end;
      continue;
    }
    if (previousLocationId !== locationId) {
      assertDefined(prevTd, 'Expected previous event for leg');
      const candidatePortCallId = event.portCallId ?? null;
      const alreadyUsed = candidatePortCallId
        ? usedPortCallIds.has(candidatePortCallId)
        : false;
      if (alreadyUsed) {
        console.error(
          `While generating legs encountered a new location with an already used port call. This hints at an invalid timeline (port call references not correctly fixed in timeline event entries in the scenario).`,
          { candidatePortCallId, locationId, time, index, event },
        );
      }
      const newPortCallId = alreadyUsed ? null : candidatePortCallId;
      portCall = { id: newPortCallId, locationId, tds: [] };
      if (!isOffHireEvent(event) && !isStaticEvent(event)) {
        event.portCallId = portCall.id;
      }
      portCalls.push(portCall);
      const from = getEventLocation(prevTd.event, locations, 'end');
      const to = getEventLocation(event, locations, 'start');
      const leg: Leg = {
        category: EventCategory.Leg,
        from: from,
        to,
        start: time,
        end: Math.min(start - 1, time + hToMs(hoursAtSea)),
        calculatedDuration: calculatedSeaDuration,
        index,
        distance,
        hoursAtSea,
        laden,
        seaDurationOverride: event.seaDurationOverride,
        channelDurationOverhead: isPortCallArrivalEventTimeData(td)
          ? td.channelDurationOverhead
          : null,
        weatherFactor: isPortCallArrivalEventTimeData(td)
          ? (td.event.weatherFactor ?? null)
          : null,
        avoidsChannels: 'legAvoidsChannels' in event && event.legAvoidsChannels,
        key: `leg-for-${key}`,
        event: {
          locationAId: from.id,
          locationBId: to.id,
          portCallId: portCall.id,
        },
        usedChannels: td.usedChannels,
        bannedChannelsInRoute: td.bannedChannelsInRoute,
        distanceUnresolved: td.distanceUnresolved,
      };
      eventsAndLegs.push(leg);
      legs.push(leg);
    } else {
      // Same location as previous event
      if (
        !isOffHireEvent(event) &&
        !isStaticEvent(event) &&
        portCall.id !== event.portCallId
      ) {
        if (
          isPortCallEvent(event) &&
          portCall.id !== null &&
          getBoundaryPairPreviousPortCallId(event) === portCall.id
        ) {
          // Consecutive-COA chain boundary: the load-side port call stays a
          // separate back-to-back port call with no leg between (SEABER-6784)
          portCall = { id: event.portCallId, locationId, tds: [] };
          portCalls.push(portCall);
        } else if (
          // Use the port call from current event only if it's already persisted and the previous port call isn't
          event.portCallId !== null &&
          (portCall.id === null || !persistedPortCallIds.has(portCall.id))
        ) {
          portCall.id = event.portCallId;
          portCall.tds.forEach((td) => {
            if (!isOffHireEvent(td.event) && !isStaticEvent(td.event)) {
              td.event.portCallId = event.portCallId;
            }
          });
        } else {
          if (event.portCallId !== portCall.id) {
            console.warn(
              `Mismatching port call ids, timeline validation may have a bug`,
              { eventOriginalPortCall: event.portCallId, event, portCall },
            );
          }
          event.portCallId = portCall.id;
        }
      }
    }

    if (getEventEndLocationId(event) !== locationId) {
      // Clear port call on off-hire which changes location
      portCall = {
        id: event.portCallId ?? generateClientId(),
        locationId,
        tds: [],
      };
    }

    portCall.tds.push(td);
    time = Math.max(time, end + 1);
    eventsAndLegs.push(td);

    if (portCall.id !== null) {
      usedPortCallIds.add(portCall.id);
    }
  }

  const eventsAndLegsWithWaits: EventsAndLegs = [];

  time = 0;
  for (let i = 0; i < eventsAndLegs.length; ++i) {
    const e = eventsAndLegs[i];
    const { end } = e;
    if (i > 0) {
      const prevEndActivity = time;
      const { start, key, event } = e;
      if (prevEndActivity + ADD_IMPLICIT_WAIT_THRESHOLD < start) {
        const location =
          'from' in e ? e.from : getEventLocation(e.event, locations);
        const tmpWaitEvent: WaitEvent = {
          start: prevEndActivity + 1,
          end: start - 1,
          calculatedDuration: undefined,
          locationName: location.name ?? '',
          locationTimeZone: location.timezone ?? null,
          index: i,
          isGenerated: true,
          eventType: ImplicitEventType.IDLE,
          key: `wait-for-${key}`,
          category: EventCategory.Wait,
          event: {
            locationId: getEventLocationId(event),
            portCallId: event.portCallId,
          },
        };
        eventsAndLegsWithWaits.push(tmpWaitEvent);
      }
    }
    eventsAndLegsWithWaits.push(e);
    time = Math.max(time, end + 1);
  }

  return { legs, eventsAndLegs: eventsAndLegsWithWaits, portCalls };
};

export type TimelineVoyage<EventType extends Events = Events> = (
  | {
      shipment: ScenarioShipment;
      orderId: null;
    }
  | {
      shipment: null;
      orderId: OrderId;
    }
) & {
  cargoEvents: TimeData<CargoEvent>[];
  implicitEvents: (Leg | WaitEvent)[];
  timeline: EventTimeline<EventType>;
};

export enum VoyageStatus {
  ONGOING = 'O',
  UPCOMING = 'U',
  COMPLETED = 'C',
}

export const getVoyages = (timeline: EventTimeline): TimelineVoyage[] => {
  const result: TimelineVoyage[] = [];

  for (const voyageGroup of timeline.voyageGroups) {
    const tds: TimeData[] = voyageGroup.events;
    const cargoEvents: TimeData<CargoEvent>[] =
      tds.filter(isCargoEventTimeData);

    const eventsByShipment = mapGroupBy(
      cargoEvents,
      (e) => e.event.order.shipmentId ?? null,
    );

    const noshipmentEventsByOrderId = mapGroupBy(
      eventsByShipment.get(null) ?? [],
      (e: TimeData<CargoEvent>) => e.event.order.id,
    );

    eventsByShipment.forEach((events, shipmentId) => {
      if (shipmentId === null) {
        // Orders without shipment handled separately
        return;
      }

      const implicitEvents = getOverlappingEvents(
        events,
        voyageGroup.eventsAndLegs.filter(isNotTimeData),
        true,
      );

      // Take shipment from the last event
      // They should be the same because events have the same shipmentId
      // events array always will be non-empty due to mapGroupBy implementation
      const shipment = events.slice().pop()?.event.order.shipment;
      assertDefined(shipment, 'shipment');

      result.push({
        shipment,
        orderId: null,
        cargoEvents: sortBy(events, (e) => e.start),
        implicitEvents,
        timeline,
      });
    });

    noshipmentEventsByOrderId.forEach((events, orderId) => {
      events.sort((a, b) => a.start - b.start);

      const implicitEvents = getOverlappingEvents(
        events,
        voyageGroup.eventsAndLegs.filter(isNotTimeData),
        true,
      );

      result.push({
        shipment: null,
        orderId,
        cargoEvents: sortBy(events, (e) => e.start),
        implicitEvents,
        timeline,
      });
    });
  }

  return result;
};

export const getVoyageStatus = (
  voyage: TimelineVoyage,
  portCallsById: Map<ClientId, ScenarioTimeline_PortCallFragment>,
  referenceTime: number = Date.now(),
): VoyageStatus => {
  const lastTd = voyage.cargoEvents[voyage.cargoEvents.length - 1];

  if (lastTd.event.portCallId !== null) {
    const lastPortCall = portCallsById.get(lastTd.event.portCallId);
    const sof = lastPortCall?.statementOfFactsByPortCallId.nodes[0];
    if (sof?.sailingTime || sof?.loadingCompleted) {
      return VoyageStatus.COMPLETED;
    }
  }

  return voyage.cargoEvents[0].start > referenceTime
    ? VoyageStatus.UPCOMING
    : VoyageStatus.ONGOING;
};

export const findVoyageGroupForEvent = (
  timeline: EventTimeline,
  event: Event,
): VoyageGroup | null => {
  const res = timeline.voyageGroups.find(
    (vg) => !!vg.events.find((td) => td.event === event),
  );
  if (!res) {
    console.error(
      `(findVoyageGroupForEvent) Attempted to find event`,
      event,
      `on the timeline ${timeline.id} but it was not found`,
    );
  }
  return res ?? null;
};

const collectDerivedTimelineEventsAndTotals = <
  T extends CoaTimeDataGroup | SpotTimeDataGroup,
  TK,
>(
  scheduleContext: ScheduleDataContext,
  groupedEvents: DefaultMap<TK, DefaultMap<number | null, T[]>>,
  topLevelId: TK,
  vesselId: number | null,
): VoyageGroup[] => {
  const result: VoyageGroup[] = [];

  groupedEvents
    .get(topLevelId)
    .get(vesselId)
    .forEach((tdGroup) => {
      const { timeData, totals, inputs } = tdGroup;
      const vessel = scheduleContext.vessels.get(vesselId ?? NaN) ?? null;

      const { portCalls, legs, eventsAndLegs } = generatePortCallsLegsAndWaits(
        timeData,
        scheduleContext.locations,
      );

      const vg: VoyageGroup = {
        events: timeData,
        eventsAndLegs,
        portCalls,
        legs,
        orders: getOrdersFromTimeDatas(timeData),
        totals,
        inputs,
        vessel,
      };

      result.push(vg);
    });

  return result;
};
export const getTimelines = (
  tcTimeDataByVessel: Map<number, TcTimeDataGroup>,
  coaTimeData: CoaTimeDataGroup[],
  spotTimeData: SpotTimeDataGroup[],
  coaContracts: readonly ScenarioTimeline_CoaFragment[],
  scheduleContext: ScheduleDataContext,
  currentUserOrgId: number | null | undefined,
  organizationsById: Map<number, ScenarioTimeline_OrganizationFragment>,
  collapsedTimelineIds: Set<EventTimeline['id']>,
  ignoreShipownerTimelines: boolean, // Hotfix for hiding COA/Spot timelines from shipowner planning
  isShipOwner: boolean,
  estimateIdOrScenario: ClientId | 'scenario',
): EventTimeline[] => {
  const { vessels } = scheduleContext;

  const timelines: EventTimeline[] = [];

  const showAllTimelines =
    typeof window !== 'undefined' && window.location.hash.includes('all');

  const currentUserIsBroker =
    currentUserOrgId && organizationsById.get(currentUserOrgId)?.broker;

  vessels.forEach((vessel) => {
    if (
      showAllTimelines ||
      vessel.organizationId === currentUserOrgId ||
      currentUserIsBroker
    ) {
      const { capacity, unit } = getPreferredCapacityQuantity(vessel);
      const timeDataGroup = tcTimeDataByVessel.get(vessel.id);
      if (estimateIdOrScenario && !timeDataGroup) {
        return;
      }

      const events = timeDataGroup?.timeData ?? [];
      const inputs = timeDataGroup?.inputs ?? null;
      const orders = getOrdersFromTimeDatas(events);

      const voyageGroup: VoyageGroup = {
        events: events,
        orders,
        ...generatePortCallsLegsAndWaits(events, scheduleContext.locations),
        totals: timeDataGroup?.totals ?? null,
        inputs,
        vessel,
      };

      const timeline: EventTimeline = {
        type: 'tc',
        vesselId: vessel.id,
        vessel,
        id:
          `vessel-${vessel.id}` +
          (estimateIdOrScenario ? `-${estimateIdOrScenario}` : ''),
        title: vessel.name ?? `Unnamed vessel ${vessel.id}`,
        subtitle: capacity && unit ? `${capacity} ${unit}` : 'Capacity N/A',
        voyageGroups: [voyageGroup],
        isPartial: false,
        estimateIdOrScenario,
      };
      if (timeline.voyageGroups.length > 1) {
        throw new Error(`TC timelines can only have 1 voyage group.`);
      }
      timelines.push(timeline);
    }
  });

  const coaTimeDataByContractAndVessel = mapGroupBy2(
    coaTimeData,
    (tdGroup) => tdGroup.coaId,
    (tdGroup) => tdGroup.vesselId,
  );

  coaContracts.forEach((coa) => {
    if (
      isShipOwner &&
      ignoreShipownerTimelines &&
      coa.chartererOrgId !== currentUserOrgId
    ) {
      return;
    }

    const timelineId = `coa-${coa.id}`;
    const isCollapsed = collapsedTimelineIds.has(timelineId);

    const voyageGroups = collectDerivedTimelineEventsAndTotals(
      scheduleContext,
      coaTimeDataByContractAndVessel,
      coa.id,
      null,
    );

    const vesselTimelines: EventTimeline<CoaEvent>[] = [];

    const contractTimeline: EventTimeline<CoaEvent> = {
      type: 'coa',
      coaId: coa.id,
      coa,
      vessel: null,
      vesselId: null,
      ownerOrganizationId: coa.shipowner?.id ?? null,
      id: timelineId,
      title: `COA ${coa.shipowner?.name ?? ''}`,
      subtitle: coa.name ?? '',
      isPartial: false,
      canCollapse: true,
      isCollapsed: isCollapsed,
      voyageGroups,
      vesselTimelineIds: [],
      estimateIdOrScenario: 'scenario',
    };

    timelines.push(contractTimeline);

    coa.vessels.nodes.filter(isDefined).forEach((coaVessel) => {
      const vessel = vessels.get(coaVessel.shipId);

      if (vessel) {
        const { capacity, unit } = getPreferredCapacityQuantity(vessel);

        const voyageGroups = collectDerivedTimelineEventsAndTotals(
          scheduleContext,
          coaTimeDataByContractAndVessel,
          coa.id,
          vessel.id,
        );

        if (
          consecutiveCoaVoyagesEnabled(vessel) &&
          voyageGroups.length > 1 &&
          !warnedConsecutiveCoaMultipleGroups
        ) {
          warnedConsecutiveCoaMultipleGroups = true;
          console.error(
            `Consecutive COA vessel timeline has more than 1 voyage group; downstream consumers will see only the first.`,
          );
        }

        const vesselTimeline: EventTimeline<CoaEvent> = {
          type: 'coa',
          coa,
          coaId: coa.id,
          vessel: vessel,
          vesselId: vessel.id,
          ownerOrganizationId: coa.shipowner?.id ?? null,
          id: `coa-${coa.id}-${vessel.id}`,
          title: vessel.name ?? 'Unnamed vessel',
          subtitle: capacity && unit ? `${capacity} ${unit}` : 'Capacity N/A',
          isPartial: false,
          canCollapse: false,
          isCollapsed: false,
          voyageGroups,
          contractTimelineId: contractTimeline.id,
          estimateIdOrScenario: 'scenario',
        };

        vesselTimelines.push(vesselTimeline);
      }
    });

    if (isCollapsed) {
      mergeTimelinesTo(contractTimeline, vesselTimelines);
    } else {
      contractTimeline.vesselTimelineIds = vesselTimelines.map(
        (timeline) => timeline.id,
      );
      timelines.push(...vesselTimelines);
    }
  });

  const spotTimeDataByOrgAndVessel = mapGroupBy2(
    spotTimeData,
    (tdGroup) => tdGroup.shipownerOrgId,
    (tdGroup) => tdGroup.vesselId,
  );
  const emptyTimelines: EventTimeline<SpotEvent>[] = [];

  for (const [ownerOrgId, m] of spotTimeDataByOrgAndVessel.entries()) {
    if (
      isShipOwner &&
      ignoreShipownerTimelines &&
      ownerOrgId === currentUserOrgId
    ) {
      continue;
    }

    const timelineId = `spot-${ownerOrgId}`;
    const vesselTimelines: EventTimeline<SpotEvent>[] = [];

    const ownerOrgName =
      typeof ownerOrgId === 'number'
        ? organizationsById.get(ownerOrgId)?.name
        : undefined;

    const isCollapsed = collapsedTimelineIds.has(timelineId);

    const voyageGroups = collectDerivedTimelineEventsAndTotals(
      scheduleContext,
      spotTimeDataByOrgAndVessel,
      ownerOrgId,
      null,
    );

    const contractTimeline: EventTimeline<SpotEvent> = {
      type: 'spot',
      ownerOrganizationId: ownerOrgId,
      vessel: null,
      vesselId: null,
      id: timelineId,
      title: `SPOT ${ownerOrgName}`,
      subtitle: '',
      isPartial: false,
      canCollapse: true,
      isCollapsed,
      voyageGroups,
      vesselTimelineIds: [],
      estimateIdOrScenario: 'scenario',
    };

    if (ownerOrgName) {
      timelines.push(contractTimeline);
    }

    for (const vesselId of m.keys()) {
      const vessel = vesselId
        ? (scheduleContext.vessels.get(vesselId) ?? null)
        : null;

      if (vessel) {
        const { capacity, unit } = getPreferredCapacityQuantity(vessel);

        const voyageGroups = collectDerivedTimelineEventsAndTotals(
          scheduleContext,
          spotTimeDataByOrgAndVessel,
          ownerOrgId,
          vesselId,
        );

        const vesselTimeline: EventTimeline<SpotEvent> = {
          type: 'spot',
          ownerOrganizationId: ownerOrgId,
          vessel,
          vesselId,
          id: `${timelineId}-${vesselId}`,
          title: vessel.name ?? 'Unnamed vessel',
          subtitle: capacity && unit ? `${capacity} ${unit}` : 'Capacity N/A',
          voyageGroups,
          isPartial: false,
          canCollapse: false,
          isCollapsed: false,
          contractTimelineId: contractTimeline.id,
          estimateIdOrScenario: 'scenario',
        };

        vesselTimelines.push(vesselTimeline);
      } else if (vessel === null && ownerOrgId === null) {
        const emptyTimeline: EventTimeline<SpotEvent> = {
          type: 'spot',
          ownerOrganizationId: null,
          vessel: null,
          vesselId: null,
          id: 'spot-null-null',
          title: isShipOwner ? 'Relet TBN' : 'Undefined spot contract',
          subtitle: '',
          voyageGroups,
          isPartial: false,
          canCollapse: false,
          isCollapsed: false,
          contractTimelineId: '',
          estimateIdOrScenario: 'scenario',
        };

        emptyTimelines.push(emptyTimeline);
      }
    }

    if (isCollapsed) {
      mergeTimelinesTo(contractTimeline, vesselTimelines);
    } else {
      contractTimeline.vesselTimelineIds = vesselTimelines.map(
        (timeline) => timeline.id,
      );
      timelines.push(...vesselTimelines);
    }
  }

  if (
    !timelines.find(
      (t) =>
        t.type == 'spot' &&
        t.ownerOrganizationId === null &&
        t.vesselId === null,
    )
  ) {
    if (
      estimateIdOrScenario === 'scenario' &&
      !emptyTimelines.find(
        (t) =>
          t.type == 'spot' &&
          t.ownerOrganizationId === null &&
          t.vesselId === null,
      )
    ) {
      const emptyTimeline: EventTimeline<SpotEvent> = {
        type: 'spot',
        ownerOrganizationId: null,
        vessel: null,
        vesselId: null,
        id: 'spot-null-null',
        title: isShipOwner ? 'Relet TBN' : 'Undefined spot contract',
        subtitle: '',
        voyageGroups: [],
        isPartial: false,
        canCollapse: false,
        isCollapsed: false,
        contractTimelineId: '',
        estimateIdOrScenario: 'scenario',
      };
      emptyTimelines.push(emptyTimeline);
    }
    timelines.push(...emptyTimelines);
  }

  const existingTimelineIds = new Set<string>();
  return timelines.filter((timeline) => {
    const alreadyExists = existingTimelineIds.has(timeline.id);
    if (alreadyExists) {
      console.error(
        `There is a duplicate group id on timelines: ${timeline.id} (vessel ${
          timeline.vessel?.name
        }, COA ${
          timeline.type === 'coa' && timeline.coa.name
        }). The latter group was filtered out but this is a sign of bad data. For example, a vessel has been added to a coa contract multiple times.`,
      );
    }
    existingTimelineIds.add(timeline.id);
    return !alreadyExists;
  });
};

/** Returns all event timedatas in one array.
 * This is only sensible for tc timelines - coa and spot may have multiple voyage groups which may overlap or be out of sequence totally. */
export function getTimelineTimedatas(timeline: EventTimeline): TimeData[] {
  return timeline.voyageGroups.flatMap((vg) => vg.events);
}

/**
 * Returns the scenario TC timelines of the given vessels that automatic planning may build a plan
 * around. Both callers assemble an external solver task, and the vessel selection they pass can come
 * straight from a planner's click, so the eligibility test lives here rather than at each call site.
 *
 * spec: specs/constraints/automatic-planning-mass-capacity.md
 */
export function filterAutomaticPlanningTimelinesByVessel(
  timelines: EventTimeline[],
  vesselIds: Set<ShipId>,
): Map<Vessel, EventTimeline> {
  return new Map(
    filteredMap(timelines, (t) =>
      isTCTimeline(t) &&
      t.estimateIdOrScenario === 'scenario' &&
      vesselIds.has(t.vesselId) &&
      isEligibleForAutomaticPlanning(t.vessel)
        ? ([t.vessel, t] as const)
        : NONE,
    ),
  );
}

export function isEstimateTimeline(
  timeline: EventTimeline,
): timeline is EventTimeline & { estimateIdOrScenario: ClientId } {
  return timeline.estimateIdOrScenario !== 'scenario';
}
